import { useCallback, useEffect, useRef, useState } from 'react';
import { track } from '../lib/analytics';
import { useContent } from '../lib/useContent';

type HeroSlide = { id: string; headline: string; body: string; alt: string };
type HeroSlidesContent = { slides: HeroSlide[] };

const DEFAULT_SLIDES: HeroSlide[] = [
  {
    id: 'hero-geyser',
    headline: 'You left the geyser on. Again.',
    body: "This house hasn't had that problem in two years. Every geyser switches itself off 30 minutes after you turn it on.",
    alt: 'Warm Indian bathroom in morning light, wall-mounted geyser with a small copper indicator glowing, middle-class Kolkata apartment',
  },
  {
    id: 'hero-sensor',
    headline: 'One sensor instead of four.',
    body: 'Our own board. Presence, temperature, humidity, pressure and light in a single unit on the wall.',
    alt: 'Warm Indian apartment living room in afternoon light — wall switchboard, teak louvred shutters, ceiling fan and bookshelf',
  },
  {
    id: 'hero-entrance',
    headline: "The house knows who's home.",
    body: 'Doors, cameras and alarm working as one system, arming itself when you leave.',
    alt: 'Indian apartment entrance at dusk, main door closing, a subtle status light near the frame',
  },
  {
    id: 'hero-nvr',
    headline: 'Seven cameras. Nothing leaves the building.',
    body: "Recording runs on your own hardware in your own home. No monthly cloud fee, no footage on someone else's server.",
    alt: 'A small home network video recorder beside a router on a shelf, tidy cabling, warm domestic light',
  },
  {
    id: 'hero-powercut',
    headline: "The internet is down. The house doesn't care.",
    body: 'Most home automation stops when the internet does. Ours runs on hardware in your home — lights, fans, sensors and automations keep working with no connection at all.',
    alt: "Kolkata street at dusk, one apartment's windows glowing warmly while the neighbourhood is dark — the home that keeps running",
  },
];

const INTERVAL_MS = 6000;

export default function Hero() {
  const { slides } = useContent<HeroSlidesContent>('hero_slides', { slides: DEFAULT_SLIDES });
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoRef = useRef(false);

  const go = useCallback((i: number) => {
    setSlide(() => (i + slides.length) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    if (!paused) {
      autoRef.current = true;
      timerRef.current = setInterval(() => go(slide + 1), INTERVAL_MS);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [paused, slide, go]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') { autoRef.current = false; go(slide + 1); }
      if (e.key === 'ArrowLeft') { autoRef.current = false; go(slide - 1); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [slide, go]);

  const onDotClick = (i: number) => {
    autoRef.current = false;
    go(i);
    track('carousel_interact', {
      slide_index: i + 1,
      interaction_method: 'dot',
    });
  };

  const onAutoAdvance = () => {
    if (autoRef.current) {
      track('carousel_interact', {
        slide_index: slide + 1,
        interaction_method: 'auto',
      });
    }
  };

  // Fire auto-advance tracking when slide changes from auto
  useEffect(() => {
    onAutoAdvance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slide]);

  return (
    <section id="hero" role="region" aria-live={paused ? 'polite' : 'off'} aria-roledescription="carousel" aria-label="What a GrihaTEK home does" tabIndex={0} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} style={{ position: 'relative', background: '#201F1D', height: 'max(560px,calc(100svh - 77px))', overflow: 'hidden' }}>
      {slides.map((s, i) => (
        <div key={i} aria-hidden={slide !== i ? true : undefined} style={{ position: 'absolute', inset: 0, transition: 'opacity .7s ease,visibility .7s', opacity: slide === i ? 1 : 0, visibility: slide === i ? 'visible' : 'hidden' }}>
          <picture style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
            <source media="(min-width: 1280px)" srcSet={`/images/${s.id}-1920.jpg`} />
            <source media="(min-width: 768px)" srcSet={`/images/${s.id}-1280.jpg`} />
            <source media="(min-width: 480px)" srcSet={`/images/${s.id}-768.jpg`} />
            <img src={`/images/${s.id}-480.jpg`} alt={s.alt} loading={i === 0 ? 'eager' : 'lazy'} fetchPriority={i === 0 ? 'high' : 'low'} width="480" height="267" style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }} />
          </picture>
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top,rgba(32,31,29,.95) 0%,rgba(32,31,29,.6) 38%,rgba(32,31,29,.08) 68%,rgba(32,31,29,.35) 100%)', pointerEvents: 'none' }} />
        </div>
      ))}
      {/* Single text block keyed on slide so React remounts it on each change,
          triggering the gt-pop fade-in and ensuring only one headline is visible. */}
      <div key={slide} className="hero-text" style={{ animation: 'gt-pop .35s ease both', zIndex: 2 }}>
        <h2 style={{ margin: '0 0 12px', fontSize: 'clamp(30px,5.5vw,54px)', fontWeight: 600, letterSpacing: '-0.015em', lineHeight: 1.05, color: '#FAF9F7' }}>{slides[slide].headline}</h2>
        <p style={{ margin: 0, fontSize: 'clamp(15.5px,2vw,18px)', lineHeight: 1.55, color: '#DDD8CF', maxWidth: 600 }}>{slides[slide].body}</p>
      </div>
      <div className="hero-controls">
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <a
            href="#rooms"
            onClick={() => track('hero_cta_click', { element_label: 'See what your home could do' })}
            style={{ background: '#B68235', color: '#201F1D', fontWeight: 600, fontSize: 15.5, padding: '13px 22px', borderRadius: 6, textDecoration: 'none', display: 'inline-block' }}
          >
            See what your home could do
          </a>
          <a
            href="/estimator"
            onClick={() => track('hero_cta_click', { element_label: 'Size your home' })}
            style={{ background: 'transparent', color: '#FAF9F7', fontWeight: 600, fontSize: 15.5, padding: '13px 22px', borderRadius: 6, textDecoration: 'none', display: 'inline-block', border: '1px solid rgba(250,249,247,.5)' }}
          >
            Size your home →
          </a>
        </div>
        <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: '.08em', color: '#C98F3D', fontFeatureSettings: '"tnum"' }}>{String(slide + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}</span>
        <div role="group" aria-label="Choose slide" style={{ display: 'flex', gap: 10 }}>
          {slides.map((_, i) => (
            <button key={i} type="button" onClick={() => onDotClick(i)} aria-label={`Go to slide ${i + 1}`} aria-current={slide === i} style={{ width: 28, height: 28, borderRadius: '50%', border: '1px solid rgba(250,249,247,.6)', background: slide === i ? '#C98F3D' : 'rgba(250,249,247,.22)', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
          ))}
        </div>
      </div>
    </section>
  );
}
